import _ from 'lodash'
import { delay } from 'redux-saga'
import { takeLatest, put, select, call } from 'redux-saga/effects'

import VALUE from 'core/data-model/constants/values'
import { asYears } from 'core/service/insured/age'
import { updateLumpSumByYear, cashFlowsToExpectedReturnComparisons } from 'core/service/investment'
import { d } from 'core/service/lib/decimal'
import { round } from 'core/service/lib/number-format'
import { getIsAgent } from 'identity/selectors'
import { getToggles } from 'quick-quote/feature-toggles'
import {
  UPDATE_BIRTHDATE,
  SELECT_GENDER,
  SELECT_NATURE_OF_DUTY,
  EDIT_TITLE_NAME,
} from 'quick-quote/insured-information/actions'
import { getAge, getGender, getAllNatureOfDutyCodes, getInsured } from 'quick-quote/insured-information/selectors'
import { TOGGLE_RIDER, EDIT_RIDER_PREMIUM } from 'quick-quote/product-common/coverage-plan/actions'
import { getAvailableRiders, getSelectedRiders } from 'quick-quote/product-common/coverage-plan/selectors'
import { SELECTED_PRODUCT } from 'quick-quote/product-selection/actions'
import {
  getSelectedDisplayProduct,
  getSelectedDisplayProductQuery,
  getSelectedBasicCategory,
  getSelectedDisplayProductCode,
} from 'quick-quote/product-selection/selectors'

import {
  EDIT_REGULAR_TOP_UP,
  EDIT_REGULAR_PREMIUM,
  SELECT_MODEL_FACTOR,
  EDIT_SUM_ASSURED,
  EDIT_LUMP_SUM,
  UPDATE_LUMP_SUMS,
  UPDATE_REGULAR_PREMIUM,
  UPDATE_REGULAR_TOP_UP,
  UPDATE_SUM_ASSURED,
  updateRegularPremium,
  updateRegularTopUp,
  updateSumAssured,
  updateLumpSums,
  updateExpectedReturnComparisons,
  updateRPUDRExpectedReturnComparisons,
  resetExpectedReturnComparisons,
  resetRPUDRExpectedReturnComparisons,
} from './actions'
import {
  getSelectedModelFactorID,
  getPreviousModelFactorID,
  getRegularPremium,
  getRegularTopUp,
  getSelectedModelFactor,
  getSumAssured,
  getLumpSum,
  getLumpSumErrors,
} from './selectors'

const DEFAULT_INFLATION_RATE = 0

export const hasRiderErrors = (selectedRiders) => {
  const errors = selectedRiders.find((r) => r.errors.length > 0)
  return !_.isUndefined(errors)
}

export const filterTPDRiderErrorForUdr = (table) => {
  if (!_.isEmpty(table) && table.TPDUDR) {
    table.TPDUDR.errors =
      table.TPDUDR.errors.length > 1 ? table.TPDUDR.errors.filter((e) => e.type !== 'VALIDATION') : table.TPDUDR.errors
    return table
  } else {
    return table
  }
}

/**
 * createProductInvestmentCoverageFunc
 * @param {ReturnType<import('core/data/data-wrapper')._DB>} dataWrapper
 * @param {ReturnType<import('core/service/insured/occupation').createInsuredOccupationService>} insuredOccupationService
 * @param {ReturnType<import('core/service/investment/coverage/validation').createInvestmentCoverageValidateService>} investmentCoverageValidationService
 * @param {ReturnType<import('core/service/investment/cash-flow/cash-flow').createInvestmentCashFlowService>} investmentCashFlowService
 * @param {ReturnType<import('core/service/investment/coverage/lumpsum').createInvestmentCoverageLumpsumService>} investmentCoverageLumpsumService
 */
export function createProductInvestmentCoverageFunc(
  dataWrapper,
  insuredOccupationService,
  investmentCoverageValidationService,
  investmentCashFlowService,
  investmentCoverageLumpsumService
) {
  const getModelFactor = async (modelFactorId) => {
    return await dataWrapper.getModelFactorById(modelFactorId)
  }

  const rppCalculation = (regularPremium, previousModelFactor, modelFactor) => {
    const result = d(regularPremium)
      .times(previousModelFactor.periods)
      .times(modelFactor.factorILP)
    return round(result.toNumber(), 0)
  }

  const rppCalculationFromSA = (sumAssured, premiumMultiplier, previousModelFactor, modelFactor) => {
    const result = d(sumAssured)
      .dividedBy(premiumMultiplier)
      .times(modelFactor.factorILP)
    return round(result.toNumber(), 0)
  }

  const rtuCalculation = (regularTopUp, previousModelFactor, modelFactor) => {
    const result = d(regularTopUp)
      .times(previousModelFactor.periods)
      .times(modelFactor.factorILP)
    return round(result.toNumber(), 0)
  }

  function* onEditRegularPremium(action) {
    // const isAgent: Boolean = yield select(getIsAgent)
    const displayProductQuery = yield select(getSelectedDisplayProductQuery)
    if (displayProductQuery.category !== VALUE.INVESTMENT) return

    const modelFactorId = yield select(getSelectedModelFactorID)
    const previousModelFactorId = yield select(getPreviousModelFactorID)

    const modelFactor = yield call(getModelFactor, modelFactorId)

    const previousModelFactor = yield call(getModelFactor, previousModelFactorId)

    const regularPremium = yield select(getRegularPremium)

    const newRegularPremium = yield call(rppCalculation, regularPremium.value, previousModelFactor, modelFactor)

    const actualRpp = action.type === SELECT_MODEL_FACTOR ? newRegularPremium : regularPremium.value

    const insuredAge = yield select(getAge)

    const validatedRegularPremium = yield call(
      investmentCoverageValidationService.validateRegularPremium,
      displayProductQuery,
      modelFactorId,
      actualRpp,
      insuredAge
    )

    yield put(updateRegularPremium(actualRpp, validatedRegularPremium.getAllMessages()))

    if (getToggles().ENABLE_MAXIMUM_SUM_ASSURED_CALCULATION_RPUL && action.type !== SELECT_MODEL_FACTOR) {
      const natureOfDutyCodes = yield select(getAllNatureOfDutyCodes)
      const occupationFactor = yield call(
        insuredOccupationService.getRiskiestOccupation,
        displayProductQuery,
        natureOfDutyCodes
      )

      const payload = {
        regularPremium: actualRpp,
        insuredAge: insuredAge,
        insuredGender: yield select(getGender),
        occupationFactor: occupationFactor,
      }

      if ([VALUE.RPUL, VALUE.RPUDR].includes(displayProductQuery.code)) {
        if (validatedRegularPremium.getAllMessages() && validatedRegularPremium.getAllMessages().length === 0) {
          const maximumSumAssured = yield call(
            investmentCoverageValidationService.getMaximumSumAssured,
            displayProductQuery,
            modelFactorId,
            payload
          )
          yield put(updateSumAssured(maximumSumAssured, []))
          return
        } else {
          yield put(updateSumAssured(0, []))
        }
      }
    }
    if (
      getToggles().ENABLE_MAXIMUM_SUM_ASSURED_CALCULATION_RPUL &&
      getToggles().ENEBLE_IWEALTHY_OPTIMISE &&
      displayProductQuery.code === VALUE.RPUL &&
      action.type === SELECT_MODEL_FACTOR
    ) {
      const natureOfDutyCodes = yield select(getAllNatureOfDutyCodes)
      const occupationFactor = yield call(
        insuredOccupationService.getRiskiestOccupation,
        displayProductQuery,
        natureOfDutyCodes
      )
      const payload = {
        regularPremium: actualRpp,
        insuredAge: insuredAge,
        insuredGender: yield select(getGender),
        occupationFactor: occupationFactor,
      }
      if (validatedRegularPremium.getAllMessages() && validatedRegularPremium.getAllMessages().length === 0) {
        const maximumSumAssured = yield call(
          investmentCoverageValidationService.getMaximumSumAssured,
          displayProductQuery,
          modelFactorId,
          payload
        )
        yield put(updateSumAssured(maximumSumAssured, []))
        return
      } else {
        yield put(updateSumAssured(0, []))
      }
    }
  }

  function* onUpdateAfterModelFactorRegularTopUp(action) {
    const displayProductQuery = yield select(getSelectedDisplayProductQuery)
    const modelFactorId = yield select(getSelectedModelFactorID)
    const previousModelFactorId = yield select(getPreviousModelFactorID)

    const modelFactor = yield call(getModelFactor, modelFactorId)

    const previousModelFactor = yield call(getModelFactor, previousModelFactorId)

    const regularTopUp = yield select(getRegularTopUp)
    const regularPremium = yield select(getRegularPremium)

    const newRegularTopUp = yield call(rtuCalculation, regularTopUp.value, previousModelFactor, modelFactor)

    const actualRtu = action.type === SELECT_MODEL_FACTOR ? newRegularTopUp : regularTopUp.value

    let premiumAmount = {
      regularTopUp: actualRtu,
      regularPremium: regularPremium.value,
    }
    const isAgent = yield select(getIsAgent)
    const validatedRegularTopUp = yield call(
      investmentCoverageValidationService.validateRegularTopUp,
      displayProductQuery,
      modelFactorId,
      premiumAmount,
      isAgent
    )
    yield put(updateRegularTopUp(actualRtu, validatedRegularTopUp.getAllMessages()))
  }

  function* onEditRegularTopUp() {
    const displayProductQuery = yield select(getSelectedDisplayProductQuery)
    const modelFactorId = yield select(getSelectedModelFactorID)
    const isAgent = yield select(getIsAgent)

    const regularTopUp = yield select(getRegularTopUp)
    const regularPremium = yield select(getRegularPremium)

    let premiumAmount = {
      regularTopUp: regularTopUp.value,
      regularPremium: regularPremium.value,
    }
    const validatedRegularTopUp = yield call(
      investmentCoverageValidationService.validateRegularTopUp,
      displayProductQuery,
      modelFactorId,
      premiumAmount,
      isAgent
    )
    yield put(updateRegularTopUp(regularTopUp.value, validatedRegularTopUp.getAllMessages()))
  }

  function* onEditSumAssured(action) {
    const displayProductQuery = yield select(getSelectedDisplayProductQuery)
    if (displayProductQuery.category !== VALUE.INVESTMENT) return

    if (_.includes([EDIT_REGULAR_PREMIUM, UPDATE_BIRTHDATE, SELECT_MODEL_FACTOR], action.type)) {
      yield call(onEditRegularPremium, action)
    }
    const regularPremium = yield select(getRegularPremium)
    const sumAssured = yield select(getSumAssured)
    if (!_.isEmpty(regularPremium.errors)) {
      yield put(updateSumAssured(sumAssured.value, []))
      return
    }

    if (displayProductQuery.code === VALUE.RPUL && action.type === SELECT_MODEL_FACTOR) {
      yield call(onUpdateAfterModelFactorRegularTopUp, action)
    }

    if (displayProductQuery.code === VALUE.SPUL) {
      yield put(updateSumAssured(regularPremium.value, []))
      return
    }

    const modelFactorId = yield select(getSelectedModelFactorID)
    const natureOfDutyCodes = yield select(getAllNatureOfDutyCodes)
    const occupationFactor = yield insuredOccupationService.getRiskiestOccupation(
      displayProductQuery,
      natureOfDutyCodes
    )

    const payload = {
      sumAssured: sumAssured.value,
      regularPremium: regularPremium.value,
      insuredAge: yield select(getAge),
      insuredGender: yield select(getGender),
      occupationFactor: occupationFactor,
      isAgent: yield select(getIsAgent),
    }
    const validatedSumAssured = yield call(
      investmentCoverageValidationService.validateSumAssured,
      displayProductQuery,
      modelFactorId,
      payload
    )
    yield put(updateSumAssured(sumAssured.value, validatedSumAssured.getAllMessages()))
  }

  function* onEditLumpSum(action) {
    const lumpSums = yield select(getLumpSum)
    const updatedLumpSums = updateLumpSumByYear(lumpSums, action.payload)
    yield call(validateLumpSums, updatedLumpSums)
  }

  function* onUpdateRegularPremium(action) {
    const lumpSums = yield select(getLumpSum)
    yield call(validateLumpSums, lumpSums)
  }

  function* validateLumpSums(lumpSums) {
    const regularPremium = yield select(getRegularPremium)
    const modelFactorId = yield select(getSelectedModelFactorID)

    const displayProductQuery = yield select(getSelectedDisplayProductQuery)

    let validatedLumpSums
    switch (displayProductQuery.code) {
      case VALUE.SPUL:
        validatedLumpSums = yield investmentCoverageLumpsumService.validateLumpSumSPUL(
          regularPremium.value,
          lumpSums,
          modelFactorId
        )
        break
      case VALUE.RPUDR:
        validatedLumpSums = yield investmentCoverageLumpsumService.validateLumpSumRPUDR(
          regularPremium.value,
          lumpSums,
          modelFactorId
        )
        break
      default:
        validatedLumpSums = yield investmentCoverageLumpsumService.validateLumpSumRPUL(
          regularPremium.value,
          lumpSums,
          modelFactorId
        )
        break
    }

    // $$FlowFixMe
    yield put(updateLumpSums(validatedLumpSums))
  }

  const isValid = ({ errors }) =>
    _(errors)
      .filter(({ type }) => type !== VALUE.ADDITIONAL_INFORMATION)
      .isEmpty()

  function* getCashFlowProps() {
    const displayProduct = yield select(getSelectedDisplayProduct)
    const age = yield select(getAge)
    const gender = yield select(getGender)
    const modelFactor = yield select(getSelectedModelFactor)
    const regularPremium = yield select(getRegularPremium)
    const regularTopUp = yield select(getRegularTopUp)
    const sumAssured = yield select(getSumAssured)
    const lumpSum = yield select(getLumpSum)
    const hasRequiredInput = regularPremium.value !== 0 && sumAssured.value !== 0
    const readyForCashFlow = hasRequiredInput && _.every([regularTopUp, regularPremium, sumAssured], isValid)

    const displayProductQuery = yield select(getSelectedDisplayProductQuery)
    const natureOfDutyCodes = yield select(getAllNatureOfDutyCodes)

    const inflationRate = DEFAULT_INFLATION_RATE
    const riders = yield select(getAvailableRiders)

    const riderOccupationFactors = yield insuredOccupationService.getRiskiestOccupationFactorForRiders(
      displayProductQuery,
      natureOfDutyCodes,
      riders
    )

    if (!readyForCashFlow) {
      return { error: 'CASHFLOW_UNREADY' }
    }

    const lumpSumErrors = yield select(getLumpSumErrors)

    if (!_.isEmpty(lumpSumErrors)) {
      return { error: 'LUMP_SUM_INCOMPLETE' }
    }

    return {
      displayProduct,
      regularPremium: regularPremium.value,
      regularTopUp: regularTopUp.value,
      lumpSum,
      age: asYears(age),
      gender,
      paymentInterval: modelFactor.periods,
      sumAssured: sumAssured.value,
      inflationRate,
      riderOccupationFactors,
      riders,
    }
  }

  function* getCashFlowByExpectedReturn() {
    const props = yield call(getCashFlowProps)

    const cashFlowByExpectedReturn = (expectedReturn) =>
      call(
        investmentCashFlowService.generateCashFlow,
        displayProduct,
        expectedReturn,
        regularPremium,
        regularTopUp,
        lumpSum,
        age,
        gender,
        paymentInterval,
        sumAssured,
        inflationRate,
        riderOccupationFactors,
        riders
      )

    const { error } = props

    if (error != null) {
      return { error }
    }

    const {
      displayProduct,
      regularPremium,
      regularTopUp,
      lumpSum,
      age,
      gender,
      paymentInterval,
      sumAssured,
      inflationRate,
      riderOccupationFactors,
      riders,
    } = props

    let result = {}
    // "3 = scenario 4 : Yield 2% Medical inflation rate 5%"
    for (let expectedReturn of [-1, 2, 5, 3]) {
      result[`${expectedReturn}`] = yield cashFlowByExpectedReturn(expectedReturn)
    }

    return result
  }

  function* doUpdateExpectedReturnComparisons() {
    const category = yield select(getSelectedBasicCategory)

    if (category !== VALUE.INVESTMENT) return

    yield call(delay, 300)

    const cashFlowByExpectedReturn = yield call(getCashFlowByExpectedReturn)
    const productCode = yield select(getSelectedDisplayProductCode)

    if (productCode === VALUE.RPUDR) {
      const selectedRiders = yield select(getSelectedRiders)
      const hasError = hasRiderErrors(selectedRiders)
      if (hasError) {
        yield put(resetRPUDRExpectedReturnComparisons())
        return
      }
    }

    if (_.isEmpty(cashFlowByExpectedReturn.error)) {
      const expectedReturnComparisons = cashFlowsToExpectedReturnComparisons(cashFlowByExpectedReturn, productCode)

      if (productCode === VALUE.RPUDR) {
        yield put(updateRPUDRExpectedReturnComparisons(expectedReturnComparisons))
      } else {
        yield put(updateExpectedReturnComparisons(expectedReturnComparisons))
      }
      return
    }

    if (cashFlowByExpectedReturn.error === 'LUMP_SUM_INCOMPLETE') return

    if (productCode === VALUE.RPUDR) {
      yield put(resetRPUDRExpectedReturnComparisons())
    } else {
      yield put(resetExpectedReturnComparisons())
    }
  }

  function* revalidateSumAssured() {
    const displayProductQuery = yield select(getSelectedDisplayProductQuery)
    if (displayProductQuery.category !== VALUE.INVESTMENT) return

    if ([VALUE.RPUL, VALUE.RPUDR].includes(displayProductQuery.code)) {
      const insured = yield select(getInsured)

      const insuredAge = yield select(getAge)

      const modelFactorID = yield select(getSelectedModelFactorID)

      const regularPremium = yield select(getRegularPremium)

      const validatedRegularPremium = yield call(
        investmentCoverageValidationService.validateRegularPremium,
        displayProductQuery,
        modelFactorID,
        regularPremium.value,
        insured.age
      )

      yield put(updateRegularPremium(regularPremium.value, validatedRegularPremium.getAllMessages()))

      if (getToggles().ENABLE_MAXIMUM_SUM_ASSURED_CALCULATION_RPUL) {
        const natureOfDutyCodes = yield select(getAllNatureOfDutyCodes)
        const occupationFactor = yield call(
          insuredOccupationService.getRiskiestOccupation,
          displayProductQuery,
          natureOfDutyCodes
        )

        const payload = {
          regularPremium: regularPremium.value,
          insuredAge: insuredAge,
          insuredGender: insured.gender,
          occupationFactor: occupationFactor,
        }

        if (validatedRegularPremium.getAllMessages() && validatedRegularPremium.getAllMessages().length === 0) {
          const maximumSumAssured = yield call(
            investmentCoverageValidationService.getMaximumSumAssured,
            displayProductQuery,
            modelFactorID,
            payload
          )

          const payloadSumAssuredValidation = {
            sumAssured: maximumSumAssured,
            regularPremium: regularPremium.value,
            insuredAge: insuredAge,
            insuredGender: insured.gender,
            occupationFactor: occupationFactor,
            isAgent: yield select(getIsAgent),
          }
          const validatedSumAssured = yield call(
            investmentCoverageValidationService.validateSumAssured,
            displayProductQuery,
            modelFactorID,
            payloadSumAssuredValidation
          )
          yield put(updateSumAssured(maximumSumAssured, validatedSumAssured.getAllMessages()))
        } else {
          yield put(updateSumAssured(0, []))
        }
      }
    }
  }

  function* onEditSumAssuredILP(action) {
    // const isAgent = yield select(getIsAgent)
    const displayProductQuery = yield select(getSelectedDisplayProductQuery)

    if (
      getToggles().ENEBLE_IWEALTHY_OPTIMISE &&
      _.includes(EDIT_SUM_ASSURED, action.type) &&
      displayProductQuery.code === VALUE.RPUL
    ) {
      const sumAssured = action.payload
      const natureOfDutyCodes = yield select(getAllNatureOfDutyCodes)
      const occupationFactor = yield insuredOccupationService.getRiskiestOccupation(
        displayProductQuery,
        natureOfDutyCodes
      )
      const modelFactorId = yield select(getSelectedModelFactorID)
      const modelFactor = yield call(getModelFactor, modelFactorId)
      const previousModelFactorId = yield select(getPreviousModelFactorID)
      const previousModelFactor = yield call(getModelFactor, previousModelFactorId)
      const insured = yield select(getInsured)
      const insuredAge = yield select(getAge)

      const regularPremiumMultiplierPayload = {
        insuredAge: insuredAge,
        insuredGender: insured.gender,
      }
      const regularPremiumMultiplier = yield call(
        investmentCoverageValidationService.getRegularPremiumMaxMultiplier,
        displayProductQuery,
        regularPremiumMultiplierPayload
      )

      const newRegularPremium = yield call(
        rppCalculationFromSA,
        sumAssured,
        regularPremiumMultiplier,
        previousModelFactor,
        modelFactor
      )

      const validateSumAssuredPayload = {
        sumAssured,
        regularPremium: newRegularPremium > VALUE.DEFAULT_RPP ? newRegularPremium : VALUE.DEFAULT_RPP,
        insuredAge: yield select(getAge),
        insuredGender: yield select(getGender),
        occupationFactor: occupationFactor,
        isAgent: yield select(getIsAgent),
      }
      const validatedSumAssured = yield call(
        investmentCoverageValidationService.validateSumAssured,
        displayProductQuery,
        modelFactorId,
        validateSumAssuredPayload
      )
      yield put(updateSumAssured(sumAssured, validatedSumAssured.getAllMessages()))

      const validatedRegularPremium = yield call(
        investmentCoverageValidationService.validateRegularPremium,
        displayProductQuery,
        modelFactorId,
        newRegularPremium,
        insuredAge
      )
      yield put(updateRegularPremium(newRegularPremium, validatedRegularPremium.getAllMessages()))
    }
    return
  }

  return {
    getModelFactor,
    rppCalculation,
    rppCalculationFromSA,
    rtuCalculation,
    onEditRegularPremium,
    onUpdateAfterModelFactorRegularTopUp,
    onEditRegularTopUp,
    onEditSumAssured,
    onEditLumpSum,
    onUpdateRegularPremium,
    validateLumpSums,
    getCashFlowProps,
    getCashFlowByExpectedReturn,
    doUpdateExpectedReturnComparisons,
    revalidateSumAssured,
    onEditSumAssuredILP,
  }
}

/**
 * @param serviceWrapper {Awaited<ReturnType<import('core/service-wrapper/wrapper').buildServiceWrapper>>}
 */
export function* watchers(serviceWrapper) {
  const { INVESTMENT } = serviceWrapper.products
  yield [
    takeLatest([SELECT_MODEL_FACTOR], INVESTMENT.onSelectedModalFactor),
    takeLatest([EDIT_REGULAR_TOP_UP, EDIT_REGULAR_PREMIUM], INVESTMENT.onEditRegularTopUp),
    takeLatest([EDIT_REGULAR_PREMIUM], INVESTMENT.onEditRegularPremium),
    takeLatest(
      [EDIT_SUM_ASSURED, UPDATE_BIRTHDATE, EDIT_REGULAR_PREMIUM, SELECT_MODEL_FACTOR, SELECT_NATURE_OF_DUTY],
      INVESTMENT.onEditSumAssured
    ),
    takeLatest(EDIT_LUMP_SUM, INVESTMENT.onEditLumpSum),
    takeLatest(UPDATE_REGULAR_PREMIUM, INVESTMENT.onUpdateRegularPremium),
    takeLatest(
      [
        SELECTED_PRODUCT,
        UPDATE_BIRTHDATE,
        SELECT_GENDER,
        SELECT_MODEL_FACTOR,
        UPDATE_REGULAR_PREMIUM,
        UPDATE_REGULAR_TOP_UP,
        UPDATE_SUM_ASSURED,
        UPDATE_LUMP_SUMS,
        TOGGLE_RIDER,
        EDIT_RIDER_PREMIUM,
        EDIT_SUM_ASSURED,
      ],
      INVESTMENT.doUpdateExpectedReturnComparisons
    ),
    takeLatest([UPDATE_BIRTHDATE, SELECT_GENDER, EDIT_TITLE_NAME], INVESTMENT.revalidateSumAssured),
  ]
}

export const sagas = watchers
